import { useCallback, useState } from 'react';

import { useToast } from '@kanda-libs/ks-design-library';
import { FinanceRate, actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';

import { formatDataForApi } from './helpers';

import { ApplyFormFieldValues } from '../../context';

export interface ApplySubmitHook {
  isSubmitting: boolean;
  error?: string;
  submitHandler: (
    id: string,
    formValues: ApplyFormFieldValues,
    rate: FinanceRate,
    successCallback?: () => void,
    errorCallback?: () => void,
    redirectFallback?: () => void,
  ) => void;
}

export default function useApplySubmit(): ApplySubmitHook {
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();
  const { showError } = useToast();

  const handleError = useCallback(
    (submitError?: string, errorCallback?: () => void) => {
      setIsSubmitting(false);
      showError(submitError ?? 'An unknown error has occured');
      setError(submitError ?? 'An unknown error has occured');
      if (errorCallback) errorCallback();
    },
    [showError],
  );

  const handleSuccess = useCallback((successCallback?: () => void) => {
    setIsSubmitting(false);
    if (successCallback) successCallback();
  }, []);

  const submitHandler = useCallback(
    (
      id: string,
      formValues: ApplyFormFieldValues,
      rate: FinanceRate,
      successCallback?: () => void,
      errorCallback?: () => void,
    ) => {
      const result = formatDataForApi(formValues, rate, false);
      if (result.type === 'error') {
        // eslint-disable-next-line
        console.log({ trace: result.trace });
        handleError(result.error, errorCallback);
        return;
      }
      const { body } = result;
      setIsSubmitting(true);
      dispatch(
        actions.putCredit({
          body,
          params: {
            id,
          },
          forceReload: true,
          onError: (submitError) =>
            handleError(submitError.message, errorCallback),
          onSuccess: () => {
            dispatch(
              actions.applyCredit({
                params: {
                  id,
                },
                onError: (submitError) =>
                  handleError(submitError.message, errorCallback),
                onSuccess: () => handleSuccess(successCallback),
              }),
            );
          },
        }),
      );
    },
    [dispatch, handleError, handleSuccess],
  );

  return {
    isSubmitting,
    error,
    submitHandler,
  };
}
